import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { saas } from "@/lib/api";
import { PageHeader, kr } from "@/components/Layout";
import { FormDialog } from "@/components/FormDialog";

const STATUS = [{ value: "utkast", label: "Utkast" }, { value: "sendt", label: "Sendt" }, { value: "akseptert", label: "Akseptert" }, { value: "avslatt", label: "Avslått" }];
const EMPTY = { customer_id: "", description: "", hourly_rate: 550, hours: 0, sqm_price: 0, sqm: 0, min_price: 0, transport: 0, discount: 0, cost_estimate: 0, payment_terms: "14 dager netto", status: "utkast" };

const calc = (q) => {
  const n = (k) => Number(q[k]) || 0;
  let sub = n("hourly_rate") * n("hours") + n("sqm_price") * n("sqm") + n("transport");
  sub = Math.max(sub, n("min_price"));
  const net = sub - sub * (n("discount") / 100);
  return { net, vat: net * 0.25, total: net * 1.25, profit: net - n("cost_estimate") };
};

export default function Quotes() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => saas.quotes.list().then(setList);
  useEffect(() => { load(); saas.customers.list().then(setCustomers); }, []);
  const live = useMemo(() => (edit ? calc(edit) : null), [edit]);

  const fields = [
    { k: "customer_id", label: "Kunde", type: "select", full: true, options: [{ value: "", label: "– Velg kunde –" }, ...customers.map((c) => ({ value: c.id, label: c.name }))] },
    { k: "hourly_rate", label: "Timepris", type: "number" }, { k: "hours", label: "Timer", type: "number" },
    { k: "sqm_price", label: "Pris per m²", type: "number" }, { k: "sqm", label: "Antall m²", type: "number" },
    { k: "transport", label: "Transport/tillegg", type: "number" }, { k: "min_price", label: "Minstepris", type: "number" },
    { k: "discount", label: "Rabatt %", type: "number" }, { k: "cost_estimate", label: "Egen kostnad (for fortjeneste)", type: "number" },
    { k: "payment_terms", label: "Betalingsbetingelser" }, { k: "status", label: "Status", type: "select", options: STATUS },
    { k: "description", label: "Beskrivelse av oppdraget", type: "textarea" },
  ];

  const save = async () => {
    const body = { ...edit, customer_id: edit.customer_id || null, customer_name: customers.find((c) => c.id === edit.customer_id)?.name || "" };
    Object.keys(EMPTY).forEach((k) => { if (typeof EMPTY[k] === "number") body[k] = Number(body[k]) || 0; });
    edit.id ? await saas.quotes.update(edit.id, body) : await saas.quotes.create(body);
    toast.success("Tilbud lagret");
    setEdit(null);
    load();
  };
  const accept = async (q) => { await saas.acceptQuote(q.id); toast.success("Akseptert – kunden er markert som vunnet"); load(); };
  const remove = async (q) => { if (window.confirm("Slette tilbudet?")) { await saas.quotes.remove(q.id); load(); } };

  return (
    <div className="p-4 sm:p-8" data-testid="quotes-page">
      <PageHeader title="Tilbud" subtitle="Priskalkulator med mva og fortjeneste">
        <Button data-testid="add-quote-button" onClick={() => setEdit({ ...EMPTY })} className="gap-1.5"><Plus className="h-4 w-4" /> Nytt tilbud</Button>
      </PageHeader>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground border-b border-border">
            <tr><th className="text-left p-3">Nr</th><th className="text-left p-3">Kunde</th><th className="text-right p-3">Eks. mva</th><th className="text-right p-3">Totalt</th><th className="text-left p-3">Status</th><th /></tr>
          </thead>
          <tbody>
            {list.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-border last:border-0">
                <td className="p-3 font-mono text-xs">{q.number}</td>
                <td className="p-3 font-semibold">{q.customer_name || "–"}</td>
                <td className="p-3 text-right">{kr(q.totals?.net)}</td>
                <td className="p-3 text-right font-bold">{kr(q.totals?.total)}</td>
                <td className="p-3">{STATUS.find((s) => s.value === q.status)?.label}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {q.status !== "akseptert" && <Button size="sm" variant="ghost" data-testid={`accept-quote-${q.id}`} onClick={() => accept(q)} className="text-[#16A34A] gap-1"><CheckCircle2 className="h-4 w-4" /> Aksepter</Button>}
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit({ ...EMPTY, ...q, customer_id: q.customer_id || "" })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(q)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">Ingen tilbud enda.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Rediger tilbud" : "Nytt tilbud"} testid="quote-dialog"
          fields={fields} value={edit} onChange={setEdit} onSubmit={save}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl bg-secondary p-3 text-sm" data-testid="quote-totals">
            <div><div className="text-xs text-muted-foreground">Eks. mva</div><b>{kr(live.net)}</b></div>
            <div><div className="text-xs text-muted-foreground">Mva 25%</div><b>{kr(live.vat)}</b></div>
            <div><div className="text-xs text-muted-foreground">Totalt</div><b className="text-primary">{kr(live.total)}</b></div>
            <div><div className="text-xs text-muted-foreground">Fortjeneste</div><b className={live.profit < 0 ? "text-destructive" : "text-[#16A34A]"}>{kr(live.profit)}</b></div>
          </div>
        </FormDialog>
      )}
    </div>
  );
}
